import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { Reveal } from "@/components/Reveal";
import { SeoFaq } from "@/components/seo/SeoFaq";
import { SeoLinkList } from "@/components/seo/SeoLinkList";
import { ProviderCard } from "@/components/search/ProviderCard";
import {
  breadcrumbListSchema,
  collectionPageSchema,
  faqPageSchema,
  jsonLdScript,
} from "@/lib/seo-schema";
import { decideIndexability, robotsMeta } from "@/lib/seo-indexability";
import { specialtySlug } from "@/lib/seo-slugs";
import { SPECIALTIES, type Provider } from "@/data/heallexa";

const PAGE_SIZE = 20;

const SITE = "https://heallexa.com";

/** Same head() shape as specialties.$slug.tsx's specialty branch, canonicalized under /providers. */
export function providerSpecialtyHeadMeta(specialty: string, slug: string) {
  const spec = specialty;
  const title = `${spec}, Specialty Overview & Top Cities | Heallexa`;
  const description = `Learn what ${spec.toLowerCase()} do, common services, related specialties, and find ${spec.toLowerCase()} in top U.S. cities on Heallexa.`;
  const url = `${SITE}/providers/${slug}`;
  const decision = decideIndexability({
    providerCount: 1,
    hasFaq: true,
    hasNearbyCities: true,
    hasRelatedProviders: true,
    internalLinkCount: 30,
  });
  return {
    meta: [
      { title },
      { name: "description", content: description },
      robotsMeta(decision.robots),
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: url },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: url }],
    scripts: [
      jsonLdScript(collectionPageSchema({ name: title, description, url })),
      jsonLdScript(
        breadcrumbListSchema([
          { name: "Home", url: "/" },
          { name: "Providers", url: "/browse" },
          { name: spec, url: `/providers/${slug}` },
        ]),
      ),
      jsonLdScript(
        faqPageSchema([
          { q: `What does a ${spec.toLowerCase().replace(/s$/, "")} do?`, a: `A ${spec.toLowerCase().replace(/s$/, "")} provides specialized healthcare services. Confirm scope of practice with the provider.` },
          { q: `How do I find a ${spec.toLowerCase().replace(/s$/, "")} near me?`, a: `Use Heallexa to filter ${spec.toLowerCase()} by city and insurance.` },
          { q: `Do ${spec.toLowerCase()} take my insurance?`, a: `Many ${spec.toLowerCase()} accept major plans like BCBS, Aetna, UnitedHealthcare, Cigna, and Medicare. Always verify with the office.` },
        ]),
      ),
    ],
  };
}

export function ProviderSpecialtyHub({
  specialty,
  providers,
  page,
  hasMore,
  total,
}: {
  specialty: string;
  providers: Provider[];
  page: number;
  hasMore: boolean;
  total: number;
}) {
  const lower = specialty.toLowerCase();
  const singular = specialty.replace(/s$/, "");

  const relatedSpecialties = SPECIALTIES.filter((s) => s !== specialty).slice(0, 12).map((s) => ({
    label: s,
    href: `/providers/${specialtySlug(s)}`,
  }));

  const end = providers.length;
  const totalLabel = total > 0
    ? `${total.toLocaleString()} result${total === 1 ? "" : "s"}`
    : `${end}${hasMore ? "+" : ""} results`;

  const faq = [
    { q: `What does a ${singular.toLowerCase()} do?`, a: `${specialty} are healthcare professionals who specialize in their field. They diagnose, treat, and help manage conditions related to their specialty.` },
    { q: `When should I see a ${singular.toLowerCase()}?`, a: `Most people see a ${singular.toLowerCase()} when referred by a primary care doctor or for ongoing specialty care. You can also self-refer in most states.` },
    { q: `Do ${lower} accept insurance?`, a: `Most ${lower} on Heallexa accept major insurance carriers. Filter by insurance to find in-network providers.` },
    { q: `How do I prepare for my first visit?`, a: `Bring your insurance card, a photo ID, a list of medications, and any relevant records or imaging from prior visits.` },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-[1200px] px-6 pt-32 md:pt-24 pb-16">
        <Breadcrumbs
          className="mb-6"
          items={[
            { name: "Home", href: "/" },
            { name: "Providers", href: "/browse" },
            { name: specialty },
          ]}
        />

        <Reveal from="up">
          <header className="mb-12">
            <p className="font-display text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">Specialty overview</p>
            <h1 className="font-display text-[clamp(1.8rem,3vw,2.5rem)] font-semibold tracking-[-0.03em] leading-[1.15] text-foreground">
              {specialty}
            </h1>
            <p className="mt-3 text-[14px] text-muted-foreground max-w-[560px]">
              Find and compare {lower} across the United States. Browse by city, filter by insurance, and read patient reviews, free on Heallexa.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/search"
                search={{ specialty }}
                className="inline-flex items-center justify-center gap-2 h-11 px-5 text-[14px] font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors rounded-md"
              >
                Search all {lower}
              </Link>
            </div>
          </header>
        </Reveal>

        {providers.length > 0 ? (
          <section className="mb-16">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
              <div>
                <p className="font-display text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-3">
                  Nationwide directory
                </p>
                <h2 className="font-display text-[clamp(1.4rem,2.4vw,1.8rem)] font-semibold tracking-[-0.03em] leading-[1.15] text-foreground">
                  {specialty}
                </h2>
              </div>
              <span className="text-[13px] text-muted-foreground tabular-nums">
                Showing <span className="font-medium text-foreground">{end}</span>{" "}
                of <span className="font-medium text-foreground">{totalLabel}</span>
              </span>
            </div>

            <ul className="grid gap-5">
              {providers.map((p, idx) => (
                <Reveal key={p.id} as="li" from="up" delay={Math.min(idx, 8) * 70}>
                  <ProviderCard p={p} />
                </Reveal>
              ))}
            </ul>

            {(hasMore || total > providers.length) && (
              <div className="flex justify-center pt-6">
                <Link
                  to="/search"
                  search={{ specialty }}
                  className="inline-flex items-center gap-1 text-[13px] h-9 px-4 rounded-md border border-border bg-background hover:bg-muted transition-colors"
                >
                  See all {totalLabel} <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            )}
          </section>
        ) : (
          <div className="mb-16 card-surface p-10 text-center text-[14px] text-muted-foreground rounded-md">
            No {lower} listed yet.{" "}
            <Link to="/search" search={{ specialty }} className="text-primary underline">
              Try the search page
            </Link>.
          </div>
        )}

        <Reveal from="up" delay={60}>
          <section className="mb-12 card-surface rounded-md p-6 sm:p-8">
            <h2 className="font-display text-[clamp(1.4rem,2.4vw,1.8rem)] font-semibold tracking-[-0.03em] leading-[1.15] text-foreground mb-3">About {lower}</h2>
            <p className="text-[14px] leading-relaxed text-muted-foreground">
              {specialty} provide specialized care across hospitals, group practices, and independent clinics. When choosing one, patients typically weigh insurance acceptance, location, and patient reviews.
            </p>
          </section>
        </Reveal>

        <Reveal from="up" delay={60}>
          <SeoLinkList heading="Related specialties" links={relatedSpecialties} columns={3} className="mb-10" />
        </Reveal>

        <Reveal from="up" delay={60}>
          <SeoFaq items={faq} className="mb-8" />
        </Reveal>
      </main>
      <FooterSection />
    </div>
  );
}
